import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { Star1 } from "iconsax-reactjs";
import "./ReviewsPanel.css";

function formatDate(value) {
  if (!value) return null;
  let date = null;
  if (typeof value.toDate === "function") date = value.toDate();
  else if (typeof value.seconds === "number") date = new Date(value.seconds * 1000);
  else date = new Date(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

// The review text, clamped to three lines with a toggle when it runs longer.
function ReviewComment({ text }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text, expanded]);

  if (!text) {
    return <p className="rv-comment muted">No written comment.</p>;
  }

  return (
    <div className="rv-comment-wrap">
      <p ref={ref} className={`rv-comment ${expanded ? "" : "is-clamped"}`}>{text}</p>
      {(overflowing || expanded) && (
        <button
          type="button"
          className="btn-link rv-more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

export default function ReviewsPanel() {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  // Which review is mid-action, so its buttons can't be pressed twice.
  const [busy, setBusy] = useState(null);

  // Listen to all pending reviews
  useEffect(() => {
    const reviewsCol = collection(db, "reviews");
    const q = query(reviewsCol, where("status", "==", "pending"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });
      setPendingReviews(list);
      setLoading(false);
    }, (err) => {
      console.error("Error listening to pending reviews:", err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleApprove = async (review) => {
    try {
      // 1. Update review status to verified
      const reviewRef = doc(db, "reviews", review.id);
      await setDoc(reviewRef, { status: "verified" }, { merge: true });

      // 2. Fetch all verified reviews for this property to compute rating and count
      const reviewsCol = collection(db, "reviews");
      const q = query(reviewsCol, where("propertyId", "==", review.propertyId), where("status", "==", "verified"));
      const snap = await getDocs(q);

      let totalRating = 0;
      let count = 0;
      let foundCurrent = false;

      snap.forEach((docSnap) => {
        totalRating += docSnap.data().rating || 0;
        count++;
        if (docSnap.id === review.id) foundCurrent = true;
      });

      // Safely insert current if index/async sync delay prevents getDocs from returning it
      if (!foundCurrent) {
        totalRating += review.rating;
        count++;
      }

      const averageRating = count > 0 ? Math.round((totalRating / count) * 10) / 10 : 5.0;

      // 3. Update property score
      const propertyRef = doc(db, "properties", review.propertyId);
      await setDoc(propertyRef, {
        rating: averageRating,
        reviews: count
      }, { merge: true });

      alert("Review approved and property score updated!");
    } catch (err) {
      console.error("Approve review failed:", err);
      alert("Failed to approve review.");
    }
  };

  const handleReject = async (review) => {
    if (window.confirm("Are you sure you want to reject this review?")) {
      try {
        const reviewRef = doc(db, "reviews", review.id);
        await setDoc(reviewRef, { status: "rejected" }, { merge: true });
        alert("Review rejected!");
      } catch (err) {
        console.error("Reject review failed:", err);
        alert("Failed to reject review.");
      }
    }
  };

  const runAction = async (review, kind, action) => {
    setBusy({ id: review.id, kind });
    try {
      await action(review);
    } finally {
      setBusy(null);
    }
  };

  const renderSkeleton = () => (
    <ul className="rv-list" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="rv-item">
          <div className="rv-reviewer">
            <span className="skeleton rv-skel-avatar" />
            <div className="rv-reviewer-text">
              <span className="skeleton rv-skel-line" />
              <span className="skeleton rv-skel-line rv-skel-short" />
            </div>
          </div>
          <div className="rv-content">
            <span className="skeleton rv-skel-line rv-skel-short" />
            <span className="skeleton rv-skel-line rv-skel-full" />
            <span className="skeleton rv-skel-line rv-skel-mid" />
          </div>
          <div className="rv-actions">
            <span className="skeleton rv-skel-btn" />
            <span className="skeleton rv-skel-btn" />
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="rv-panel">
      <section className="card rv-queue" aria-labelledby="rv-queue-title">
        <div className="card-header">
          <div className="rv-queue-title">
            <h2 id="rv-queue-title" className="card-title">Waiting for approval</h2>
            {!loading && pendingReviews.length > 0 && (
              <span className="chip chip-warning">{pendingReviews.length} pending</span>
            )}
          </div>
          <span className="rv-queue-note">Approving a review adds it to the listing's rating.</span>
        </div>

        {loading ? (
          renderSkeleton()
        ) : pendingReviews.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon"><Star1 size={22} variant="Linear" /></span>
            <h3>No reviews waiting</h3>
            <p>
              When app users review a listing, the review waits here until you approve or reject it.
              Approved reviews show on the listing and count toward its rating.
            </p>
          </div>
        ) : (
          <ul className="rv-list">
            {pendingReviews.map((rev) => {
              const rating = Number(rev.rating) || 0;
              const reviewer = rev.userName || "Unnamed user";
              const listing = rev.propertyTitle || rev.propertyId;
              const date = formatDate(rev.createdAt);
              const isBusy = busy && busy.id === rev.id;
              return (
                <li key={rev.id} className="rv-item">
                  <div className="rv-reviewer">
                    <span className="avatar">
                      {rev.userAvatar ? (
                        <img src={rev.userAvatar} alt="" />
                      ) : (
                        (rev.userName?.charAt(0) || "U").toUpperCase()
                      )}
                    </span>
                    <div className="rv-reviewer-text">
                      <span className="rv-name" title={reviewer}>{reviewer}</span>
                      <span className="rv-listing" title={listing}>{listing}</span>
                    </div>
                  </div>

                  <div className="rv-content">
                    <div className="rv-meta">
                      <span className="rv-rating" role="img" aria-label={`Rated ${rating} out of 5`}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star1
                            key={s}
                            size={16}
                            variant="Bold"
                            className={`rv-star ${s <= rating ? "is-filled" : ""}`}
                            aria-hidden="true"
                          />
                        ))}
                        <span className="rv-rating-num" aria-hidden="true">{rating.toFixed(1)}</span>
                      </span>
                      {date && <span className="rv-date">{date}</span>}
                    </div>
                    <ReviewComment text={rev.comment} />
                  </div>

                  <div className="rv-actions">
                    <button
                      type="button"
                      className="btn-danger btn-sm"
                      onClick={() => runAction(rev, "reject", handleReject)}
                      disabled={!!isBusy}
                    >
                      {isBusy && busy.kind === "reject" && <span className="btn-spinner" />}
                      Reject
                    </button>
                    <button
                      type="button"
                      className="btn-tonal btn-sm"
                      onClick={() => runAction(rev, "approve", handleApprove)}
                      disabled={!!isBusy}
                    >
                      {isBusy && busy.kind === "approve" && <span className="btn-spinner" />}
                      Approve
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
